/**
 * Voltaje de batería → porcentaje de carga.
 *
 * Regla oficial del fabricante (Xiaoma IoT MQTT Integration Specification
 * V1.4, §6.4): interpolación lineal por tramos entre 3080 mV (0%), 3680 mV
 * (10%), 4060 mV (90%) y 4200 mV (100%), con un decimal.
 *
 * ── Por qué está duplicada ────────────────────────────────────────────
 * El backend ya la implementa en apps/core/src/modules/devices/sensor-view.ts
 * y la aplica a la ÚLTIMA lectura, que es la que llega en `sensor.battery`.
 * Pero el histórico (`GET /sensors/:imei/history`) devuelve la telemetría
 * cruda con `battery_voltage` en voltios, sin convertir — convertir cada
 * punto de una serie de 30 días en el servidor sería trabajo inútil cuando
 * la gráfica es lo único que los consume.
 *
 * Si el fabricante corrige la curva, hay que tocar los dos lados. Los valores
 * viven en una sola constante en cada archivo justamente para que el cambio
 * sea de una línea.
 */
const BATTERY_CURVE: Array<readonly [mv: number, pct: number]> = [
  [3080, 0], [3680, 10], [4060, 90], [4200, 100],
];

/**
 * Voltaje a partir del cual la curva ya marca 100 %.
 *
 * Se deriva de la propia curva para que no se desincronice si el fabricante
 * la corrige. Sirve para explicar en la UI por qué una batería que se
 * mantiene arriba de este valor dibuja una línea plana en 100 %: está
 * saturada la escala, no congelado el dato.
 */
export const BATTERY_FULL_VOLTS = BATTERY_CURVE[BATTERY_CURVE.length - 1]![0] / 1000;

/**
 * @param volts voltaje en VOLTIOS (el decoder ya divide los mV del equipo).
 * @returns porcentaje 0–100, o `null` si el valor no es un número usable.
 *          Nunca 0 por defecto: un 0% pintaría una alarma de batería
 *          agotada sobre un dato que no existe.
 */
export function batteryPercentFromVolts(volts: number | null | undefined): number | null {
  if (typeof volts !== 'number' || !Number.isFinite(volts)) return null;
  const mv = volts * 1000;
  if (mv < BATTERY_CURVE[0]![0]) return 0;
  if (mv >= BATTERY_CURVE[BATTERY_CURVE.length - 1]![0]) return 100;
  for (let i = 0; i < BATTERY_CURVE.length - 1; i++) {
    const [mv0, p0] = BATTERY_CURVE[i]!;
    const [mv1, p1] = BATTERY_CURVE[i + 1]!;
    if (mv >= mv0 && mv < mv1) {
      return Math.round((p0 + ((mv - mv0) / (mv1 - mv0)) * (p1 - p0)) * 10) / 10;
    }
  }
  return null;
}
